import { useEffect, useState } from 'react';
import {
  BottomSheet,
  Button,
  NumPad,
  SegmentedControl,
} from '../../components';
import type { Account, AccountType } from '../../data/types';
import { formatCurrency } from '../../lib/currency';

const TYPE_OPTIONS: { value: AccountType; label: string }[] = [
  { value: 'checking', label: 'Checking' },
  { value: 'savings', label: 'Savings' },
  { value: 'credit', label: 'Credit' },
];

export type AccountSheetPayload =
  | { mode: 'add'; input: { name: string; type: AccountType; balance: number } }
  | { mode: 'edit'; id: string; patch: Partial<Account> };

type Props = {
  /** 'add' to create, an existing Account to edit, null to close. */
  mode: 'add' | Account | null;
  currency: string;
  onClose: () => void;
  onSave: (payload: AccountSheetPayload) => void;
};

/**
 * Bottom sheet for create + edit of a manual Account row. Name is a
 * plain text input; type is a 3-way segmented control; balance opens a
 * NumPad sub-sheet for currency entry.
 */
export function AccountSheet({ mode, currency, onClose, onSave }: Props) {
  const open = mode !== null;
  const editing = mode && mode !== 'add' ? mode : null;

  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('checking');
  const [balance, setBalance] = useState<number>(0);
  const [balanceOpen, setBalanceOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setType(editing.type);
      setBalance(editing.balance);
    } else {
      setName('');
      setType('checking');
      setBalance(0);
    }
    setBalanceOpen(false);
  }, [open, editing]);

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    if (editing) {
      onSave({
        mode: 'edit',
        id: editing.id,
        patch: { name: trimmed, type, balance },
      });
    } else {
      onSave({
        mode: 'add',
        input: { name: trimmed, type, balance },
      });
    }
  };

  return (
    <BottomSheet
      open={open}
      onClose={onClose}
      title={editing ? 'Edit account' : 'Add account'}
    >
      <div className="px-screen-x py-4 flex flex-col gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Name
          </span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Chase Checking"
            className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] text-text-primary outline-none focus:ring-2 focus:ring-accent"
          />
        </label>

        <div className="flex flex-col gap-1.5">
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Type
          </span>
          <SegmentedControl
            options={TYPE_OPTIONS}
            value={type}
            onChange={setType}
          />
        </div>

        <button
          type="button"
          onClick={() => setBalanceOpen(true)}
          className="flex flex-col gap-1.5 text-left"
        >
          <span className="text-[12px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
            Balance
          </span>
          <span className="rounded-button bg-surface-secondary px-3 py-2.5 text-[15px] font-mono tabular-nums text-text-primary">
            {formatCurrency(balance, { currency })}
          </span>
        </button>

        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!name.trim()}
          onClick={submit}
        >
          {editing ? 'Save' : 'Add account'}
        </Button>
      </div>

      <BottomSheet
        open={balanceOpen}
        onClose={() => setBalanceOpen(false)}
        title="Balance"
      >
        <NumPad
          initialValue={balance}
          currency={currency}
          onDone={(v) => {
            setBalance(v);
            setBalanceOpen(false);
          }}
        />
      </BottomSheet>
    </BottomSheet>
  );
}
